import React, { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import { Feature } from "ol";
import { AccordionItem } from "../../Elements/Accordion";
import { executeQuery } from "../../Map/rest";
import { useAppContext } from "../../Contexts/AppContext";
import { config } from "../../Map/config";

const maxResults = 7;

export interface IQuery {
    service: string;
    url: string;
    layerId: string;
    params: {
        f: string;
        where: string;
        outFields: string;
    };
}

export const Search = (): JSX.Element => {
    const [text, setText] = useState<string>("");
    const [results, setResults] = useState<Feature[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [selected, setSelected] = useState<Feature>();

    const { setLocateFeature } = useAppContext();

    useEffect(() => {
        setLocateFeature(selected);
    }, [selected]);

    const executeSearch = useCallback(async () => {
        if (!text) {
            return;
        }
        setLoading(true);
        const queries = buildQueries(text);
        const queryResults = await doQuery(queries);
        setResults(queryResults.slice(0, maxResults));
        setLoading(false);
    }, [text]);

    const onChange = (e) => {
        setResults([]);
        setSelected(undefined);
        setText(e.target.value);
    };

    const onSelect = (selectedFeature: Feature) => {
        setSelected(selectedFeature);
        setText(getValue(selectedFeature));
    };

    const onClear = () => {
        setText("");
        setSelected(undefined);
        setResults([]);
    };

    const onKeyDown = (e) => {
        e.key === "Enter" && executeSearch();
    };

    return (
        <AccordionItem
            title="Meklēt"
            defaultExpanded={Date.now()}
            changeHeight={Date.now()}
            key="Meklēt pēc novada, pagasta vai zemes vienības kadastra apzīmējuma"
        >
            <div className="draw-toolbar search-bar">
                <div className="symbol-label" onClick={executeSearch}>
                    <span className="icon-zemesnoma-search"></span>
                </div>
                <div className="search-input">
                    <input
                        type="text"
                        placeholder="Novads, pagasts vai kad.apz."
                        value={text}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                    />
                    {loading && (
                        <div className="search-clear">
                            <span className="icon-zemesnoma-spinner rotating"></span>
                        </div>
                    )}
                    {!loading && text && (
                        <div className="search-clear" onClick={onClear}>
                            <span className="icon-zemesnoma-close-popup"></span>
                        </div>
                    )}
                    <div className={classNames("search-list", { none: selected })}>
                        {results.map((result, i) => (
                            <div className="list-option" key={"option-" + i} onClick={() => onSelect(result)}>
                                {getValue(result)}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            {selected && (
                <div className="search-selected" onClick={() => setLocateFeature(selected)}>
                    <div className="symbol-label">
                        <span className="icon-zemesnoma-zoom-in"></span>
                    </div>
                    <div>{getValue(selected)}</div>
                </div>
            )}
        </AccordionItem>
    );
};

const buildQueries = (searchText: string) => {
    const queries: IQuery[] = [];
    config.search.services.map((service) =>
        service.searchLayers.map((searchLayer) =>
            queries.push({
                service: service.id,
                url: service.url,
                layerId: searchLayer.layerId,
                params: {
                    f: "json",
                    where: "UPPER(" + searchLayer.field + ") LIKE '%" + searchText.toUpperCase() + "%'",
                    outFields: "*"
                }
            })
        )
    );
    return queries;
};

const doQuery = async (queries: IQuery[]) => {
    const results = [];
    for (const query of queries) {
        const features = await executeQuery(query);
        results.push(...features);
    }
    return results;
};

const getValue = (feature: Feature) => {
    let value;
    const searchConf = config.search.services.find((s) => s.id === feature.get("service"));
    for (const searchLayer of searchConf.searchLayers) {
        value = feature.get(searchLayer.field);
        if (value) {
            break;
        }
    }
    return value;
};
